<template>
  <div class="page page--vehicle-detail w-full">
    <SharedContentSection
      v-if="vehiclePage?.content"
      v-for="(section, index) in vehiclePage?.content"
      :key="index"
      :content="section"
      :index="index"
      :is-first="index === 0"
      :is-last="index === (vehiclePage?.content?.length || 0) - 1"
    />
  </div>
</template>

<script setup lang="ts">
  import type { StrapiResponse, SiteOption } from '~/types/strapi';
  import type { GraphQLPage, GraphQLCarDetail, GraphQLModel } from '~/types/graphql';
  import type { PageEventData } from '~/types/types';
  import { buildSchemaOrgCar } from '~/composables/useVehicleSchemaOrgCar';
  import { buildMasterImageUrl } from '#shared/imgproxy';

  const siteOptions = inject('siteOptions') as Ref<StrapiResponse<SiteOption>> | undefined;

  const { locale, t } = useI18n();
  const router = useRouter();

  const { previewQuery, previewKeyPart } = usePreview();
  const { data: vehiclePage } = await useAsyncData(
    `vehiclePage-${previewKeyPart}${locale.value}`,
    async () => {
      const page = await $fetch<GraphQLPage>('/api/page', {
        query: {
          slug: siteOptions?.value?.data?.general_vehicle_detail_page?.slug,
          locale: locale.value,
          ...previewQuery,
        },
      });

      return page;
    },
    {
      watch: [locale],
    }
  );

  const slug = computed(() => router.currentRoute.value.params.slug);
  // Fetching car detail data
  let carNotFound = ref(false);
  const { data: carDetail } = await useAsyncData(
    `carDetail-${slug.value}-${locale.value}`,
    async () => {
      const carDetail = await $fetch<GraphQLCarDetail>('/api/vehicles/details', {
        query: {
          slug: slug.value,
          locale: locale.value,
        },
      }).catch(err => {
        if (err.statusCode === 404) {
          carNotFound.value = true;
          return null;
        }
        // otherwise throw the error
        throw err;
      });

      return carDetail;
    },
    {
      watch: [locale, slug],
    }
  );

  watch(
    carNotFound,
    (newValue, _oldValue) => {
      if (newValue) {
        navigateTo(`/autos-kaufen/${router.currentRoute.value.params.make}/${router.currentRoute.value.params.body_style}`, {
          redirectCode: 301,
        });
      }
    },
    { immediate: true }
  );

  // if (!carDetail.value || ['Reserviert', 'Reserviert vom Kunden'].includes(carDetail.value?.vehicle_status || '')) {
  //   // redirect to body_style page with a 302 status
  //   navigateTo(`/autos-kaufen/${router.currentRoute.value.params.make}/${router.currentRoute.value.params.body_style}`, {
  //     redirectCode: 301,
  //   });
  // }

  // Providing car detail data to the page and components
  provide('carDetail', carDetail.value);

  // fetch model data
  const { data: model } = await useAsyncData(
    `model-${carDetail.value?.model_group || slug.value || ''}-${locale.value}`,
    async () => {
      if (!carDetail.value?.model_group) {
        return null;
      }

      const model = await $fetch<GraphQLModel>('/api/vehicles/model', {
        query: {
          name: carDetail.value?.model_group || '',
          locale: locale.value,
        },
      }).catch(err => {
        if (err.statusCode === 404) {
          return null;
        }
        throw err;
      });

      return model;
    },
    {
      watch: [locale, () => carDetail.value?.model_group, slug],
    }
  );

  // providing model data to the page and components
  provide('carModel', model.value);

  const route = useRoute();
  const runtimeConfig = useRuntimeConfig();

  // The canonical "master" imgproxy variant for each vehicle image. These are
  // the URLs we expose to crawlers (og:image, twitter:image, JSON-LD
  // ImageObject) so Google has one representative URL per source photo
  // instead of the ~14 responsive-srcset variants the renderer emits.
  //
  // Route through useVehicleImages so we pick up filenames written by the Go
  // syncer (vehicle_image_files → /uploads/<file>) and fall back to legacy
  // Strapi-uploaded images automatically.
  const masterImageUrls = computed<string[]>(() => {
    const images = carDetail.value ? useVehicleImages(carDetail.value as any).filter(img => img?.url) : [];
    return images.map(img => buildMasterImageUrl(img.url, runtimeConfig.public.imgproxyUrl as string | undefined)).filter(Boolean);
  });

  const seo = computed(() => {
    const siteUrl = runtimeConfig.public.siteURL || '';

    const title =
      vehiclePage.value?.seo?.metaTitle.replace('$[$make$]', carDetail.value?.make?.name || '').replace('$[$model$]', carDetail.value?.model || '') ||
      vehiclePage.value?.title ||
      'Page';
    const description =
      vehiclePage.value?.seo?.metaDescription
        .replace('$[$make$]', carDetail.value?.make?.name || '')
        .replace('$[$model$]', carDetail.value?.model || '')
        .replace('$[$year_of_manufacture$]', String(carDetail.value?.year_of_manufacture) || '')
        .replace('$[$mileage$]', String(carDetail.value?.mileage) || '') || 'Page';
    const ogImage = masterImageUrls.value?.[0] || '';

    const ogUrl = `${siteUrl}${route.path}`;

    let structuredData: any = undefined;
    const originalStructuredData = vehiclePage.value?.seo?.structuredData;

    if (originalStructuredData && carDetail.value) {
      try {
        let stringifiedStructuredData = JSON.stringify(originalStructuredData);

        // Get image URLs for structured data (canonical imgproxy master variant)
        const imageUrls = masterImageUrls.value || [];

        // Helper function to escape replacement values for JSON
        const escapeForJson = (value: string): string => {
          // Use JSON.stringify to properly escape the value, then remove surrounding quotes
          return JSON.stringify(value).slice(1, -1);
        };

        const replacements = [
          ['make', carDetail.value?.make?.name || ''],
          ['model', carDetail.value?.model || ''],
          ['model_group', carDetail.value?.model_group || ''],
          ['vehicle_type', carDetail.value?.body_type_general || ''],
          ['description', carDetail.value?.description || ''],
          ['motiondata_id', String(carDetail.value?.internal_vehicle_no) || ''],
          ['IMAGE_URL_1', imageUrls[0] || ''],
          ['IMAGE_URL_2', imageUrls[1] || ''],
          ['URL_zu_Bild_3', imageUrls[2] || ''],
          ['vin', carDetail.value?.vin || ''],
          ['year_of_manufacture', String(carDetail.value?.year_of_manufacture) || ''],
          ['first_registration', carDetail.value?.first_registration || ''],
          ['mileage', String(carDetail.value?.mileage) || ''],
          ['fuel_type', carDetail.value?.fuel_type || ''],
          ['transmission_type', carDetail.value?.transmission_type || ''],
          ['exterior_color', carDetail.value?.exterior_color || ''],
          ['power_kw', String(carDetail.value?.power_kw) || ''],
          ['CANONICAL_URL', `${siteUrl}${route.path}`],
          ['price', String(carDetail.value?.asking_price) || ''],
          ['VEHICLE_AVAILABILITY', carDetail.value?.vehicle_status === 'Verkauft' ? 'https://schema.org/Sold' : 'https://schema.org/InStock'],

          // TODO: Get proper values!
          ['DEALERSHIP_NAME', 'Herbert Seidl GmbH'],
          ['DEALERSHIP_URL', siteUrl || ''],
          ['DEALERSHIP_STREET', 'Hofgasse 1'],
          ['DEALERSHIP_CITY', '1010 Wien'],
          ['DEALERSHIP_POSTCODE', '1010'],
        ];

        replacements.forEach(([key, value]) => {
          // Escape the value to handle control characters, quotes, etc.
          const escapedValue = escapeForJson(value || '');
          stringifiedStructuredData = stringifiedStructuredData.replace(new RegExp(`\\$\\[\\$${key}\\$\\]`, 'g'), escapedValue);
        });

        structuredData = JSON.parse(stringifiedStructuredData);
      } catch (error) {
        console.error('Error processing structured data:', error);
        // If processing fails, don't use structured data (will fall back to default)
        structuredData = undefined;
      }
    } else if (originalStructuredData) {
      // If carDetail is not available yet, don't set structuredData (will be processed on next computed run)
      structuredData = undefined;
    }

    if (structuredData === undefined && carDetail.value) {
      structuredData = buildSchemaOrgCar(carDetail.value, {
        pageUrl: ogUrl,
        imageUrls: masterImageUrls.value || [],
      });
    }

    const seo = {
      title,
      description,
      ogImage,
      ogUrl,
      ogType: 'website' as const,
      ogLocale: locale.value,
      structuredData,
    };

    return seo;
  });

  // set up seo for the detail page
  watch(
    seo,
    newVal => {
      route.meta.title = newVal.title;
      route.meta.metaDescription = newVal.description;
      // Always update structuredData, even if undefined (to clear old data)
      route.meta.structuredData = newVal.structuredData;

      route.meta.ogTitle = newVal.title;
      route.meta.ogDescription = newVal.description;
      route.meta.ogImage = newVal.ogImage;
      route.meta.ogUrl = newVal.ogUrl;
      route.meta.ogType = newVal.ogType;
      route.meta.ogLocale = newVal.ogLocale;
    },
    { immediate: true }
  );

  // Render og:image / twitter:image with the master imgproxy URL directly.
  // The shared layout's useStrapiSeo only reads nested `openGraph.ogImage.url`
  // — the flat `route.meta.ogImage` we set above never reaches it — so without
  // this block no vehicle-specific og:image would be emitted at all.
  useSeoMeta({
    ogImage: () => seo.value.ogImage || undefined,
    twitterImage: () => seo.value.ogImage || undefined,
    twitterCard: () => (seo.value.ogImage ? 'summary_large_image' : undefined),
  });

  const { push } = useDataLayer();

  const trackPageViewed = () => {
    const eventData: PageEventData = {
      event: 'page_view',
      page_type: 'vehicle_detail',
      page_location: window.location.href,
      page_title: seo.value?.title || '',
      vehicle_id: carDetail.value?.internal_vehicle_no?.toString() || '',
      make: carDetail.value?.make?.name,
      model: carDetail.value?.model,
      manufactured_at: carDetail.value?.year_of_manufacture?.toString() || '',
      price: carDetail.value?.asking_price,
      fuel_type: carDetail.value?.fuel_type,
    };
    push(eventData);
  };

  onMounted(() => {
    trackPageViewed();
  });

  watch(carDetail, () => {
    nextTick(() => {
      trackPageViewed();
    });
  });
</script>

<style lang="scss" scoped></style>

<style lang="scss">
  .page.page--vehicle-detail {
    @media print {
      section:not(:has(.car-detail-container)) {
        display: none !important;
      }
    }
  }
</style>
